import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toastr from 'toastr';
import './TrainingDashboard.css';

const TrainingDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('paths');
  const [trainingPaths, setTrainingPaths] = useState([]);
  const [myEnrollments, setMyEnrollments] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_BASE = '/api/training';

  useEffect(() => {
    loadData();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const sessionId = localStorage.getItem('sessionId');
      const headers = {
        'x-session-id': sessionId,
        'Content-Type': 'application/json'
      };

      switch (activeTab) {
        case 'paths':
          const pathsRes = await fetch(`${API_BASE}/paths`, { headers });
          if (pathsRes.ok) {
            const data = await pathsRes.json();
            setTrainingPaths(data.paths || []);
          }
          break;

        case 'my-learning':
          const enrollmentsRes = await fetch(`${API_BASE}/enrollments`, { headers });
          if (enrollmentsRes.ok) {
            const data = await enrollmentsRes.json();
            setMyEnrollments(data.enrollments || []);
          }
          break;

        case 'leaderboard':
          const leaderRes = await fetch(`${API_BASE}/leaderboard?limit=20`, { headers });
          if (leaderRes.ok) {
            const data = await leaderRes.json();
            setLeaderboard(data.leaderboard || []);
          }

          const myStatsRes = await fetch(`${API_BASE}/leaderboard/me`, { headers });
          if (myStatsRes.ok) {
            const data = await myStatsRes.json();
            setMyStats(data.entry);
          }
          break;

        case 'badges':
          const badgesRes = await fetch(`${API_BASE}/badges`, { headers });
          if (badgesRes.ok) {
            const data = await badgesRes.json();
            setMyBadges(data.badges || []);
          }
          break;
        default:
          break;
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (pathId) => {
    try {
      const sessionId = localStorage.getItem('sessionId');
      const res = await fetch(`${API_BASE}/enrollments`, {
        method: 'POST',
        headers: {
          'x-session-id': sessionId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ trainingPathId: pathId })
      });

      if (res.ok) {
        toastr.success('Successfully enrolled!');
        loadData();
      } else {
        const data = await res.json();
        toastr.error(data.error || 'Enrollment failed');
      }
    } catch (err) {
      toastr.error('Failed to enroll');
    }
  };

  const renderTrainingPaths = () => (
    <div className="training-paths-grid">
      {trainingPaths.length === 0 ? (
        <div className="empty-state">
          <p>No training paths available yet</p>
        </div>
      ) : (
        trainingPaths.map(path => (
          <div key={path._id} className="training-path-card">
            <div className="path-header">
              <h3>{path.title}</h3>
              <span className={`difficulty-badge ${path.difficulty}`}>
                {path.difficulty}
              </span>
            </div>
            <p className="path-description">{path.description}</p>
            <div className="path-meta">
              <div className="meta-item">
                <span className="label">Challenges:</span>
                <span className="value">{path.challenges?.length || 0}</span>
              </div>
              <div className="meta-item">
                <span className="label">Points:</span>
                <span className="value">{path.totalPoints || 0}</span>
              </div>
              {path.estimatedHours && (
                <div className="meta-item">
                  <span className="label">Est. Hours:</span>
                  <span className="value">{path.estimatedHours}h</span>
                </div>
              )}
            </div>
            <button
              className="cyber-btn primary"
              onClick={() => handleEnroll(path._id)}
            >
              Enroll Now
            </button>
          </div>
        ))
      )}
    </div>
  );

  const renderMyLearning = () => (
    <div className="my-learning-container">
      {myEnrollments.length === 0 ? (
        <div className="empty-state">
          <p>You haven't enrolled in any training paths yet</p>
          <button className="cyber-btn" onClick={() => setActiveTab('paths')}>
            Browse Training Paths
          </button>
        </div>
      ) : (
        <div className="enrollments-list">
          {myEnrollments.map(enrollment => (
            <div key={enrollment._id} className="enrollment-card">
              <div className="enrollment-header">
                <h3>{enrollment.trainingPathId?.title || 'Training Path'}</h3>
                <span className={`status-badge ${enrollment.status}`}>
                  {enrollment.status}
                </span>
              </div>
              <div className="progress-section">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${enrollment.progress || 0}%` }}
                  />
                </div>
                <span className="progress-text">{enrollment.progress || 0}% Complete</span>
              </div>
              <div className="enrollment-stats">
                <div className="stat">
                  <span className="stat-label">Points Earned:</span>
                  <span className="stat-value">{enrollment.pointsEarned || 0}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Started:</span>
                  <span className="stat-value">
                    {new Date(enrollment.startedAt).toLocaleDateString()}
                  </span>
                </div>
                {enrollment.completedAt && (
                  <div className="stat">
                    <span className="stat-label">Completed:</span>
                    <span className="stat-value">
                      {new Date(enrollment.completedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
              <button className="cyber-btn">Continue Learning</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderLeaderboard = () => (
    <div className="leaderboard-container">
      {myStats && (
        <div className="my-stats-card">
          <h3>My Ranking</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Rank</span>
              <span className="stat-value">#{myStats.rank || 'N/A'}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Points</span>
              <span className="stat-value">{myStats.totalPoints || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Challenges</span>
              <span className="stat-value">{myStats.challengesCompleted || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Badges</span>
              <span className="stat-value">{myStats.badgesEarned || 0}</span>
            </div>
          </div>
        </div>
      )}

      <div className="leaderboard-table">
        <h3>Top Performers</h3>
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>User</th>
              <th>Points</th>
              <th>Challenges</th>
              <th>Badges</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((entry, index) => (
              <tr
                key={entry._id}
                className={entry.userId === user?.id ? 'current-user' : ''}
              >
                <td>#{index + 1}</td>
                <td>{entry.userId?.email || 'User'}</td>
                <td>{entry.totalPoints || 0}</td>
                <td>{entry.challengesCompleted || 0}</td>
                <td>{entry.badgesEarned || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderBadges = () => (
    <div className="badges-grid">
      {myBadges.length === 0 ? (
        <div className="empty-state">
          <p>No badges earned yet. Complete challenges to earn badges!</p>
        </div>
      ) : (
        myBadges.map(badge => (
          <div key={badge._id} className="badge-card">
            <h3>{badge.name}</h3>
            <p>{badge.description}</p>
            <div className="badge-meta">
              <span className="points">+{badge.points} points</span>
              {badge.earnedAt && (
                <span className="earned-date">
                  Earned {new Date(badge.earnedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="training-dashboard">
      <header className="training-header">
        <div className="header-content">
          <div>
            <h1>Training Center</h1>
            <p>Enhance your security skills</p>
          </div>
          {user?.role === 'admin' && (
            <button
              className="cyber-btn"
              onClick={() => navigate('/admin/dashboard')}
            >
              Admin Panel
            </button>
          )}
        </div>
      </header>

      <nav className="training-tabs">
        <button
          className={`tab ${activeTab === 'paths' ? 'active' : ''}`}
          onClick={() => setActiveTab('paths')}
        >
          Training Paths
        </button>
        <button
          className={`tab ${activeTab === 'my-learning' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-learning')}
        >
          My Learning
        </button>
        <button
          className={`tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          Leaderboard
        </button>
        <button
          className={`tab ${activeTab === 'badges' ? 'active' : ''}`}
          onClick={() => setActiveTab('badges')}
        >
          Badges
        </button>
      </nav>

      <div className="training-content">
        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading-state">
            <div className="cyber-spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'paths' && renderTrainingPaths()}
            {activeTab === 'my-learning' && renderMyLearning()}
            {activeTab === 'leaderboard' && renderLeaderboard()}
            {activeTab === 'badges' && renderBadges()}
          </>
        )}
      </div>
    </div>
  );
};

export default TrainingDashboard;
